import * as angular from "ng-file-upload";
import * as Common from "../Common/modules";
import * as moment from 'moment';
import * as Models from "../Models";
import { IMilestoneInvoiceSubmissionModel } from "../Models/MilestoneInvoice/milestoneInvoiceSubmission";
import { Reference } from "../Shared/Constants";

export interface IProcessReviewInvoiceApiClient {
  submitProcessReviewInvoice: (submission: Models.ProcessReviewInvoice.InvoiceSubmission.IInvoiceSubmissionModel, processReviewType: string) => ng.IPromise<any>;
  getInvoiceDetails: (igsCustomerNumber: string, processReviewType: string) => ng.IPromise<any>;
  getAdditionalInvoiceDetails: (igsCustomerNumber: string) => ng.IPromise<Models.ProcessReviewInvoice.InvoiceSubmission.InvoiceDetails>;
  getMilestoneDetails: (igsCustomerNumber: string) => ng.IPromise<Models.MilestoneInvoice.MilestoneInvoiceSubmission.MilestoneDetails>;
  respondToProcessReview: (igsCustomerNumber: string, approving: boolean, rejectionReason: string, upload: File, processReviewType: string) => ng.IPromise<any>;
  approveRefundReview: (igsCustomerNumber: string) => ng.IPromise<any>;
}

export class ProcessReviewInvoiceApiClient implements IProcessReviewInvoiceApiClient {
  public static $inject = ["igs.apiClientService", "Upload", "igs.maskingService"];

  constructor(private igsApiClientService: Common.ApiClientService.Igs.Common.IApiClientService,
              private uploadService: angular.angularFileUpload.IUploadService,
              private maskingService: any) {
  }

  public getInvoiceDetails = (igsCustomerNumber: string, processReviewType: string): ng.IPromise<any> => {
    switch (processReviewType) {
      case Reference.Reference.milestoneProcessReviewType:
        return this.getMilestoneDetails(igsCustomerNumber);
      case Reference.Reference.additionalInvoiceProcessReviewType:
        return this.getAdditionalInvoiceDetails(igsCustomerNumber);
      default:
        break;
    }
  }

  public getAdditionalInvoiceDetails = (igsCustomerNumber: string): ng.IPromise<Models.ProcessReviewInvoice.InvoiceSubmission.InvoiceDetails> => {
    return this.igsApiClientService.get(`AdditionalInvoiceDetails/${igsCustomerNumber}`);
  }

  public getMilestoneDetails = (igsCustomerNumber: string): ng.IPromise<Models.MilestoneInvoice.MilestoneInvoiceSubmission.MilestoneDetails> => {
    return this.igsApiClientService.get(`MilestoneDetails/${igsCustomerNumber}`);
  }

  public respondToProcessReview = (igsCustomerNumber: string, approving: boolean, rejectionReason: string, upload: File, processReviewType: string): ng.IPromise<any> => {
    return this.uploadService.upload({
      url: "api/ProcessReviewInvoiceResponse",
      method: "POST",
      data: {
        igsCustomerNumber,
        approved: approving,
        rejectionReason,
        upload,
        invoiceType: processReviewType,
      },
    });
  }

  public submitProcessReviewInvoice = (submission: Models.ProcessReviewInvoice.InvoiceSubmission.IInvoiceSubmissionModel, processReviewType: string): ng.IPromise<any> => {
    const fileNameEnd = submission.document.file && submission.document.file.name ? submission.document.file.name.lastIndexOf(".") : -1;
    const fileExtensionNoDot = fileNameEnd !== -1 ? submission.document.file.name.substring(fileNameEnd + 1) : "";

    const data: any = {
      invoiceNumber: submission.invoiceNumber,
      igsCustomerNumber: submission.igsCustomerNumber,
      invoiceType: processReviewType,
      invoiceFile: submission.document.file,
      invoiceFileName: submission.document.userInputFileName,
      invoiceFileExtension: fileExtensionNoDot,
      invoiceContentType: submission.document.file ? submission.document.file.type : "",
    };

    if (processReviewType === Reference.Reference.milestoneProcessReviewType) {
      const milestoneSubmission = submission as IMilestoneInvoiceSubmissionModel;

      const ptoLetterNameEnd = milestoneSubmission.ptoLetter.file && milestoneSubmission.ptoLetter.file.name
        ? milestoneSubmission.ptoLetter.file.name.lastIndexOf(".")
        : -1;

      const ptoLetterExtensionNoDot = ptoLetterNameEnd !== -1 
        ? milestoneSubmission.ptoLetter.file.name.substring(ptoLetterNameEnd + 1)
        : "";

      data.ptoDate = getFormattedDate(milestoneSubmission.ptoDate);
      data.ptoLetterFile = milestoneSubmission.ptoLetter.file;
      data.ptoLetterFileName = milestoneSubmission.ptoLetter.userInputFileName;
      data.ptoLetterFileExtension = ptoLetterExtensionNoDot;
      data.ptoLetterContentType = milestoneSubmission.ptoLetter.file ? milestoneSubmission.ptoLetter.file.type : "";
    }

    return this.maskingService.maskPromise(this.uploadService.upload({
      url: "api/ProcessReviewInvoice",
      method: "POST",
      data
    }));
  }

  public approveRefundReview = (igsCustomerNumber: string): ng.IPromise<any> => {
    const url = `RefundReview/Approve/${igsCustomerNumber}/`;
    return this.igsApiClientService.post(url);
  }
}

// SOL-5578: This utility function was added as a quick fix for
// timezone issue when submitting PTO date for M3. This should not
// be considered a long-term fix or approach for addressing timezone
// related bugs.
function getFormattedDate(date: Date | null | string): string | null {
  if (!date) {
    return null;
  }

  return moment(date).format('YYYY-MM-DD');
}
